import { useState } from 'react';
import type { ReactNode } from 'react';
import { slugForTenantId } from '@yourstore/domain';
import { useAction, useApi, useRead } from '../../context';
import { Confirm, Failure, Loading } from '../../bits';
import { Chip, Panel, useToast } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import { DomainStatus } from '../../../wire';
import type { TenantDomain } from '../../../wire';
import { Steps } from './Steps';
import { shortDate } from './words';

/**
 * **Address** — where their booking site answers (spec §6.4.2), as steps.
 *
 * Replaces the Domains table and its "Move to" links. Each address shows its
 * progress — Added → Pointing here → Secure → Live — derived from the stored
 * status: waiting for their DNS is *Added*; the certificate being made means
 * their DNS points here and *Secure* is under way; live is *Live*. Whether it
 * is actually served is said in its own words beside it, because a status
 * that sounds nearly done is still not served.
 *
 * ## What this card must not let anyone believe
 *
 * **Marking a step does not do the step.** The status decides whether we
 * answer on the address; it does not create their DNS record and it does not
 * make the certificate. So every move is confirmed, and the dialog says what
 * changes for guests — above all that marking an address live before its
 * certificate is serving makes browsers refuse it, which looks like an outage.
 * Every move the table offered is still here, as a step forward or back.
 *
 * ## What it cannot do
 *
 * Remove an address, or take one from another business: both take a working
 * address off the air (and sign out everybody signed in on it), so both are
 * done from the command line where the address is typed again. The server
 * refuses a takeover and its refusal is shown as it came. The card says so in
 * plain words, so a missing button reads as a decision.
 *
 * The zero-setup address needs no row and is shown first: a business with no
 * address of its own still has a booking site.
 */
export function AddressCard({ tenantId }: { tenantId: string }): ReactNode {
  const api = useApi();
  const t = useT();
  const toast = useToast();
  const add = useAction();
  const move = useAction();
  const page = useRead(() => api.domains(tenantId), [tenantId]);

  const [adding, setAdding] = useState(false);
  const [hostname, setHostname] = useState('');
  const [pending, setPending] = useState<{ domain: TenantDomain; status: DomainStatus } | null>(
    null,
  );

  const submit = async (): Promise<void> => {
    let said = '';
    const ok = await add.run(async () => {
      const answer = await api.addDomain(tenantId, { hostname });
      said =
        answer.created === true
          ? t('site.address.added', { hostname: answer.hostname })
          : t('site.address.updated', { hostname: answer.hostname });
    });
    if (ok) {
      toast(said);
      setAdding(false);
      setHostname('');
      page.reload();
    }
  };

  const confirmMove = async (): Promise<void> => {
    if (pending === null) return;
    const { domain, status } = pending;
    const ok = await move.run(async () => {
      await api.setDomainStatus(tenantId, domain.hostname, status);
    });
    if (ok) {
      toast(t('site.address.moved', { hostname: domain.hostname, step: stepName(t, status) }));
      setPending(null);
      page.reload();
    }
  };

  const rows = page.value?.domains ?? [];
  const hasDefault = rows.some((d) => d.kind === 'default');

  return (
    <Panel title={t('site.address.title')} className="site-card">
      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {pending === null && move.failure !== null ? <Failure failure={move.failure} /> : null}

      {page.value === null ? (
        page.loading ? (
          <Loading what={t('loading.domains')} />
        ) : null
      ) : (
        <ul className="site-addresses">
          {hasDefault ? null : (
            <li className="site-address">
              <div className="site-address__head">
                <span>
                  <strong>{t('site.address.defaultName')}</strong>{' '}
                  <code className="site-address__host">{slugForTenantId(tenantId)}</code>
                </span>
                <Chip tone="ok">{t('site.address.served')}</Chip>
              </div>
              <p className="muted">{t('site.address.defaultNote')}</p>
            </li>
          )}
          {rows.map((domain) => (
            <Address
              key={domain.hostname}
              domain={domain}
              onMove={(status) => {
                move.clear();
                setPending({ domain, status });
              }}
            />
          ))}
        </ul>
      )}

      {adding ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="field field--wide">
            <span className="field__label">{t('site.address.hostname')}</span>
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="book.lovespa.com"
              aria-label={t('site.address.hostname')}
            />
            <span className="field__hint">{t('site.address.hostnameHint')}</span>
          </label>
          {add.failure !== null ? <Failure failure={add.failure} /> : null}
          <div className="row-actions">
            <button type="button" className="button button--sm" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="button button--primary button--sm"
              disabled={add.busy || hostname.trim() === ''}
            >
              {add.busy ? t('common.saving') : t('site.address.save')}
            </button>
          </div>
        </form>
      ) : (
        <div className="row-actions">
          <button
            type="button"
            className="button button--sm"
            onClick={() => {
              add.clear();
              setAdding(true);
            }}
          >
            {t('site.address.add')}
          </button>
        </div>
      )}

      <p className="muted site-note">{t('site.address.cliOnly')}</p>

      {pending !== null ? (
        <Confirm
          tone={pending.status === 'live' ? 'primary' : 'danger'}
          title={t('site.address.moveTitle', {
            hostname: pending.domain.hostname,
            step: stepName(t, pending.status),
          })}
          consequence={`${consequenceOf(t, pending.status)} ${t('site.address.notTheWorld')}`}
          confirmLabel={moveLabel(t, pending.status)}
          busy={move.busy}
          failure={move.failure}
          onConfirm={() => void confirmMove()}
          onCancel={() => {
            move.clear();
            setPending(null);
          }}
        />
      ) : null}
    </Panel>
  );
}

/** The step a status stands for, as an index into the four. */
export function reachedOfDomain(status: string): number {
  switch (status) {
    case 'pending_dns':
      return 0;
    case 'issuing':
      return 2;
    case 'live':
      return 3;
    default:
      return 0;
  }
}

function Address({
  domain,
  onMove,
}: {
  domain: TenantDomain;
  onMove: (status: DomainStatus) => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const order = DomainStatus.options;
  const here = order.indexOf(domain.status);

  return (
    <li className="site-address">
      <div className="site-address__head">
        <code className="site-address__host">{domain.hostname}</code>
        {domain.served ? (
          <Chip tone="ok">{t('site.address.served')}</Chip>
        ) : (
          <Chip>{t('site.address.notServed')}</Chip>
        )}
      </div>
      <Steps
        label={t('site.address.stepsLabel', { hostname: domain.hostname })}
        steps={[
          t('site.address.step.added'),
          t('site.address.step.pointing'),
          t('site.address.step.secure'),
          t('site.address.step.live'),
        ]}
        reached={reachedOfDomain(domain.status)}
      />
      <p className="muted">
        {meaningOf(t, domain.status)}
        {domain.kind === 'default' ? ` ${t('site.address.ours')}` : ''}
        {domain.verified_at === null
          ? ''
          : ` ${t('site.address.verified', { day: shortDate(domain.verified_at, locale) })}`}
      </p>
      <div className="row-actions">
        {order
          .filter((status) => status !== domain.status)
          .map((status) => (
            <button
              key={status}
              type="button"
              className={`button button--sm${order.indexOf(status) > here ? '' : ' button--quiet'}`}
              onClick={() => onMove(status)}
            >
              {moveLabel(t, status)}
            </button>
          ))}
      </div>
    </li>
  );
}

/*
 * The words per status, keyed by a status token that is **not** translated.
 * A `switch` so the compiler checks each branch, and so a status from a newer
 * server reads as an empty sentence rather than as its token.
 */

function stepName(t: T, status: string): string {
  switch (status) {
    case 'pending_dns':
      return t('site.address.step.added');
    case 'issuing':
      return t('site.address.step.secure');
    case 'live':
      return t('site.address.step.live');
    default:
      return '';
  }
}

function moveLabel(t: T, status: string): string {
  switch (status) {
    case 'pending_dns':
      return t('site.address.to.waiting');
    case 'issuing':
      return t('site.address.to.pointing');
    case 'live':
      return t('site.address.to.live');
    default:
      return '';
  }
}

function meaningOf(t: T, status: string): string {
  switch (status) {
    case 'pending_dns':
      return t('site.address.meaning.waiting');
    case 'issuing':
      return t('site.address.meaning.securing');
    case 'live':
      return t('site.address.meaning.live');
    default:
      return '';
  }
}

function consequenceOf(t: T, status: string): string {
  switch (status) {
    case 'pending_dns':
      return t('site.address.consequence.waiting');
    case 'issuing':
      return t('site.address.consequence.securing');
    case 'live':
      return t('site.address.consequence.live');
    default:
      return '';
  }
}
